import React from 'react'
import { X } from 'lucide-react'

interface TaskType {
  id: string
  taskType: string
  total?: number
}

interface RecordTimeModalProps {
  isOpen: boolean
  title: string
  taskTypes: TaskType[]
  selectedTaskTypeId: string | null
  onTaskTypeSelect: (taskTypeId: string) => void
  onSave: (value: number) => void
  onCancel: () => void
}

const RecordTimeModal: React.FC<RecordTimeModalProps> = ({
  isOpen,
  title,
  taskTypes,
  selectedTaskTypeId,
  onTaskTypeSelect,
  onSave,
  onCancel,
}) => {
  const [hours, setHours] = React.useState<string>('')
  const [minutes, setMinutes] = React.useState<string>('')
  const [isHoursMode, setIsHoursMode] = React.useState<boolean>(true)

  const handleNumberClick = (num: string) => {
    if (isHoursMode) {
      setHours((prev) => prev + num)
    } else {
      // Only allow up to 2 digits for minutes and max 59
      if (minutes.length < 2) {
        const newMinutes = minutes + num
        const minutesNum = parseInt(newMinutes)
        if (minutesNum <= 59) {
          setMinutes(newMinutes)
        }
      }
    }
  }

  const handleBackspace = () => {
    if (isHoursMode) {
      setHours((prev) => prev.slice(0, -1))
    } else {
      setMinutes((prev) => prev.slice(0, -1))
    }
  }

  const handleToggleMode = () => {
    setIsHoursMode((prev) => !prev)
  }

  const handleSave = () => {
    if (!selectedTaskTypeId) return

    const hoursNum = parseFloat(hours) || 0
    const minutesNum = parseFloat(minutes) || 0

    if (hoursNum > 0 || minutesNum > 0) {
      // Convert to total seconds (e.g., 2 hours 30 minutes = 9000 seconds)
      const totalSeconds = (hoursNum * 3600) + (minutesNum * 60)
      onSave(totalSeconds)
      setHours('')
      setMinutes('')
      setIsHoursMode(true)
    }
  }

  const handleCancel = () => {
    setHours('')
    setMinutes('')
    setIsHoursMode(true)
    onCancel()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <button
            onClick={handleCancel}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Task Selection Buttons - Always show */}
          <div className="mb-4 flex gap-2 flex-wrap">
            {taskTypes.map((taskType) => (
              <button
                key={taskType.id}
                onClick={() => onTaskTypeSelect(taskType.id)}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${
                  selectedTaskTypeId === taskType.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {taskType.taskType}
                {taskType.total !== undefined && ` (${taskType.total})`}
              </button>
            ))}
          </div>

          {/* Time Display */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-600 mb-2">Time</p>
            <div className="flex items-center justify-center gap-1">
              <div className={`flex-1 text-right ${isHoursMode ? 'bg-blue-100' : ''} rounded px-2 py-1`}>
                <input
                  type="text"
                  value={hours || '0'}
                  readOnly
                  className="w-full text-right text-3xl font-bold text-gray-900 bg-transparent outline-none"
                  placeholder="0"
                />
              </div>
              <span className="text-3xl font-bold text-gray-900">:</span>
              <div className={`flex-1 text-left ${!isHoursMode ? 'bg-blue-100' : ''} rounded px-2 py-1`}>
                <input
                  type="text"
                  value={minutes.padStart(2, '0') || '00'}
                  readOnly
                  className="w-full text-left text-3xl font-bold text-gray-900 bg-transparent outline-none"
                  placeholder="00"
                />
              </div>
            </div>
            <div className="text-center text-xs text-gray-500 mt-2">
              {isHoursMode ? 'Entering Hours' : 'Entering Minutes'}
            </div>
          </div>

          {/* Numeric Keypad */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            {/* Numbers 1-9 */}
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
              <button
                key={num}
                onClick={() => handleNumberClick(num.toString())}
                className="bg-green-50 hover:bg-green-100 text-gray-900 font-semibold py-3 rounded-lg transition-colors active:bg-green-200"
              >
                {num}
              </button>
            ))}

            {/* Toggle Hours/Minutes */}
            <button
              onClick={handleToggleMode}
              className="bg-blue-50 hover:bg-blue-100 text-blue-700 font-semibold py-3 rounded-lg transition-colors active:bg-blue-200 col-span-1 text-xs"
            >
              {isHoursMode ? 'H→M' : 'M→H'}
            </button>

            {/* Zero */}
            <button
              onClick={() => handleNumberClick('0')}
              className="bg-green-50 hover:bg-green-100 text-gray-900 font-semibold py-3 rounded-lg transition-colors active:bg-green-200"
            >
              0
            </button>

            {/* Backspace */}
            <button
              onClick={handleBackspace}
              className="bg-red-50 hover:bg-red-100 text-red-700 font-semibold py-3 rounded-lg transition-colors active:bg-red-200 col-span-1"
            >
              ⌫
            </button>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <button
              onClick={handleCancel}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!selectedTaskTypeId || ((hours.trim() === '' || hours === '0') && (minutes.trim() === '' || minutes === '0'))}
              className="flex-1 px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RecordTimeModal
